/**
 * Column builders. The column `kind` decides the cell renderer, so money columns are
 * always MoneyValue, statuses always StatusBadge, IDs always ReferenceId.
 */
import type { CellContext } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { BankIdentity, MoneyValue, ReferenceId, StatusBadge, type RegistryName, type RegistryValue } from '@/business';
import { Tooltip } from '@/design-system';
import { formatDate, formatDateTime, formatInteger, formatRelative } from '@/shared/lib/format';

import type { DataColumn } from './types';

type Accessor<T, V> = (row: T) => V;

interface Common {
  sortKey?: string;
  width?: number;
  hideable?: boolean;
  compact?: boolean;
  total?: ReactNode;
}

/** `detail`: one muted line under the reference (e.g. product · imported). */
export function idColumn<T>(id: string, header: string, accessor: Accessor<T, string>, to: Accessor<T, string | undefined>, opts: Common & { detail?: Accessor<T, string | null> } = {}): DataColumn<T> {
  const { detail, ...meta } = opts;
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c: CellContext<T, unknown>) => {
      const d = detail?.(c.row.original);
      return d ? (
        <span className="flex flex-col leading-tight">
          <ReferenceId value={accessor(c.row.original)} to={to(c.row.original)} />
          <span className="text-meta text-ink-muted">{d}</span>
        </span>
      ) : (
        <ReferenceId value={accessor(c.row.original)} to={to(c.row.original)} />
      );
    },
    meta: { kind: 'id', sticky: true, hideable: false, compact: true, label: header, width: 150, ...meta },
  };
}

export function textColumn<T>(id: string, header: string, accessor: Accessor<T, ReactNode>, opts: Common & { secondary?: Accessor<T, ReactNode>; truncate?: number } = {}): DataColumn<T> {
  const { secondary, truncate, ...meta } = opts;
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c) => {
      const v = accessor(c.row.original);
      const s = secondary?.(c.row.original);
      return (
        <span className="flex min-w-0 flex-col leading-tight" style={truncate ? { maxWidth: truncate } : undefined}>
          <span className="truncate" dir="auto">{v ?? '—'}</span>
          {s ? <span className="truncate text-meta text-ink-muted">{s}</span> : null}
        </span>
      );
    },
    meta: { kind: 'text', hideable: true, label: header, ...meta },
  };
}

export function moneyColumn<T>(id: string, header: string, accessor: Accessor<T, string | null | undefined>, opts: Common & { kind?: 'amount' | 'difference' | 'clawback'; emptyReason?: string } = {}): DataColumn<T> {
  const { kind, emptyReason, ...meta } = opts;
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c) => <MoneyValue value={accessor(c.row.original)} kind={kind ?? 'amount'} emptyReason={emptyReason} />,
    meta: { kind: 'money', align: 'end', hideable: true, label: header, width: 130, ...meta },
  };
}

export function numberColumn<T>(id: string, header: string, accessor: Accessor<T, number | null>, opts: Common & { suffix?: string; tone?: Accessor<T, 'warning' | 'negative' | undefined> } = {}): DataColumn<T> {
  const { suffix, tone, ...meta } = opts;
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c) => {
      const v = accessor(c.row.original);
      const t = tone?.(c.row.original);
      return v === null ? (
        <span className="text-ink-muted">—</span>
      ) : (
        <span className={t === 'negative' ? 'font-semibold text-negative' : t === 'warning' ? 'font-medium text-warning' : undefined}>
          {formatInteger(v)}
          {suffix ?? ''}
        </span>
      );
    },
    meta: { kind: 'number', align: 'end', hideable: true, label: header, width: 70, ...meta },
  };
}

export function dateColumn<T>(id: string, header: string, accessor: Accessor<T, string | null>, opts: Common & { style?: 'date' | 'datetime' | 'relative' } = {}): DataColumn<T> {
  const { style = 'date', ...meta } = opts;
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c) => {
      const v = accessor(c.row.original);
      if (!v) return <span className="text-ink-muted">—</span>;
      if (style === 'relative')
        return (
          <Tooltip content={formatDateTime(v)}>
            <time dateTime={v} className="text-ink-secondary">
              {formatRelative(v)}
            </time>
          </Tooltip>
        );
      return <time dateTime={v} className="tabular">{style === 'datetime' ? formatDateTime(v) : formatDate(v)}</time>;
    },
    meta: { kind: style === 'relative' ? 'relative' : style, hideable: true, label: header, width: style === 'datetime' ? 150 : 110, ...meta },
  };
}

export function statusColumn<T, R extends RegistryName>(id: string, header: string, registry: R, accessor: Accessor<T, RegistryValue<R>>, opts: Common & { detail?: Accessor<T, string | null | undefined> } = {}): DataColumn<T> {
  const { detail, ...meta } = opts;
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c) => <StatusBadge registry={registry} value={accessor(c.row.original)} detail={detail?.(c.row.original)} />,
    meta: { kind: 'status', hideable: true, label: header, width: 150, ...meta },
  };
}

export function bankColumn<T>(id: string, header: string, accessor: Accessor<T, string>, name?: Accessor<T, string | undefined>, opts: Common = {}): DataColumn<T> {
  return {
    id,
    header,
    accessorFn: accessor,
    cell: (c) => <BankIdentity code={accessor(c.row.original)} name={name?.(c.row.original)} />,
    meta: { kind: 'bank', hideable: true, label: header, width: 90, ...opts },
  };
}

export function customColumn<T>(id: string, header: string, render: (row: T) => ReactNode, opts: Common & { align?: 'start' | 'end'; kind?: 'text' | 'status' } = {}): DataColumn<T> {
  const { align, kind, ...meta } = opts;
  return { id, header, cell: (c) => render(c.row.original), meta: { kind: kind ?? 'text', align: align ?? 'start', hideable: true, label: header, ...meta } };
}
